import React from "react";
import { Link } from "react-router-dom";
import { dismissOnboarding } from "../lib/api";

/**
 * OnboardingChecklist — persistent progress card on the player dashboard.
 *
 * Reads `state` (from `/api/onboarding/state`) and renders the four
 * post-welcome milestones with inline jump links. Auto-hides once all
 * steps are done OR the user dismisses it.
 *
 * Props:
 *   state: { steps_completed: string[], completed_at?, dismissed? }
 *   onChange: fn() — re-fetch state after dismiss
 *   onToast: fn(msg)
 */
const ITEMS = [
  {
    step: "primary_game",
    title: "Set your primary game",
    cta: "Edit profile",
    href: "/profile",
    icon: "fa-gamepad",
    accent: "#14b8a6",
  },
  {
    step: "avatar",
    title: "Pick or generate an avatar",
    cta: "Choose avatar",
    href: "/profile",
    icon: "fa-user-astronaut",
    accent: "#14b8a6",
  },
  {
    step: "joined_league",
    title: "Join your first league",
    cta: "Browse leagues",
    href: "/leagues",
    icon: "fa-tower-broadcast",
    accent: "#14b8a6",
  },
  {
    step: "sent_message",
    title: "Say hi in league chat",
    cta: "Open a league",
    href: "/leagues",
    icon: "fa-comments",
    accent: "#14b8a6",
  },
];

export function OnboardingChecklist({ state, onChange, onToast }) {
  if (!state) return null;
  if (state.completed_at || state.dismissed) return null;

  const done = new Set(state.steps_completed || []);
  const completed = ITEMS.filter((i) => done.has(i.step)).length;
  const total = ITEMS.length;
  const pct = Math.round((completed / total) * 100);

  const skip = async () => {
    try {
      await dismissOnboarding();
      onToast?.("Checklist hidden — you can re-show it from your profile.");
      onChange?.();
    } catch (_e) {
      onToast?.("Couldn't hide checklist.");
    }
  };

  return (
    <div
      data-testid="onboarding-checklist"
      className="glass-strong rounded-3xl border-2 border-[#14b8a6]/40 p-5 sm:p-6 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 -z-10 opacity-20 bg-gradient-to-br from-[#14b8a6]/40 to-[#14b8a6]/30"
      />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-list-check mr-1" />SETUP CHECKLIST
          </p>
          <p className="font-display text-xl text-white mt-1">
            {completed === 0 ? "First steps on the ladder" : `${completed}/${total} done — keep going`}
          </p>
        </div>
        <button
          type="button"
          onClick={skip}
          data-testid="onboarding-checklist-skip"
          className="text-[10px] tracking-widest text-white/45 hover:text-white px-2 py-1"
        >
          SKIP
        </button>
      </div>

      <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          data-testid="onboarding-checklist-progress"
          className="h-full bg-gradient-to-r from-[#14b8a6] via-[#14b8a6] to-[#14b8a6] transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="mt-4 space-y-2">
        {ITEMS.map((it) => {
          const isDone = done.has(it.step);
          return (
            <li
              key={it.step}
              data-testid={`onboarding-step-${it.step}`}
              className={`flex items-center gap-3 rounded-2xl p-3 border transition ${
                isDone
                  ? "border-[#14b8a6]/30 bg-[#14b8a6]/5 text-white/65"
                  : "border-white/10 bg-black/30 hover:border-white/30"
              }`}
            >
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                  isDone
                    ? "bg-[#14b8a6]/20 text-[#14b8a6]"
                    : "bg-black/40 text-white/55"
                }`}
                style={!isDone ? { color: it.accent } : undefined}
              >
                <i className={`fas ${isDone ? "fa-check" : it.icon}`} aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${isDone ? "line-through text-white/55" : "text-white"}`}>
                  {it.title}
                </p>
              </div>
              {!isDone && (
                <Link
                  to={it.href}
                  data-testid={`onboarding-step-${it.step}-cta`}
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-black/50 border border-white/20 text-white hover:border-white/50 hover:bg-black/70 transition-colors shrink-0"
                >
                  {it.cta} <i className="fas fa-arrow-right ml-1" />
                </Link>
              )}
            </li>
          );
        })}
      </ul>

      {completed === total && (
        <p
          data-testid="onboarding-checklist-done"
          className="mt-4 text-center text-[#14b8a6] text-sm font-bold tracking-widest"
        >
          🏆 ALL CHECKS COMPLETE — WELCOME TO THE LADDER
        </p>
      )}
    </div>
  );
}

export default OnboardingChecklist;
